import React, { useState, useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { Edit, Delete, Email, VpnKey, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  getAllAccounts,
  sendPasswordReset,
  getResetLink,
  UserData,
  AccountData,
} from '../../services/userManagementService';

// Role options
const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer', description: 'Can view data' },
  { value: 'editor', label: 'Editor', description: 'Can view and edit data' },
  { value: 'manager', label: 'Manager', description: 'Can manage data and users' },
  { value: 'admin', label: 'Admin', description: 'Full account access' },
  { value: 'superadmin', label: 'Super Admin', description: 'Global access' },
];

interface CreateUserForm {
  email: string;
  name: string;
  isSuperadmin: boolean;
  accountId: string;
  role: string;
}

interface EditUserForm {
  id: string;
  email: string;
  name: string;
  isSuperadmin: boolean;
  accountId: string | null;
  accountName: string | null;
  role: string | null;
  status: string;
  ssoExempt: boolean;
  lastLogin: string | null;
  loginCount: number;
  createdAt: string;
}

export const UsersManagement: React.FC = () => {
  const { data: currentUser } = useUser();
  const isAdmin = useIsAdmin();
  const [users, setUsers] = useState<UserData[]>([]);
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    email: '',
    name: '',
    isSuperadmin: false,
    accountId: '',
    role: 'viewer',
  });
  const [editForm, setEditForm] = useState<EditUserForm | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [sendingResetTo, setSendingResetTo] = useState<string | null>(null);
  const [copyingLinkFor, setCopyingLinkFor] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'user' | 'account' | 'role' | 'status' | 'lastLogin'>('lastLogin');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Fetch users and accounts
  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, accountsData] = await Promise.all([
        getAllUsers(),
        getAllAccounts(),
      ]);
      
      console.log('[UsersManagement] Fetched users:', usersData.map(u => ({ 
        email: u.email, 
        isSuperadmin: u.isSuperadmin, 
        isOwner: u.isOwner 
      })));
      
      setUsers(usersData);
      setAccounts(accountsData);
      
      // Set default account for create form
      // Non-superadmins can only create users in their own account
      if (currentUser && !currentUser.isAdmin) {
        setCreateForm(prev => ({ ...prev, accountId: currentUser.accountId || '' }));
      } else if (accountsData.length > 0 && !createForm.accountId) {
        setCreateForm(prev => ({ ...prev, accountId: accountsData[0].id }));
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetchData();
    }
  }, [currentUser?.id]);

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all'
      || (filterRole === 'superadmin' ? user.isSuperadmin : (!user.isSuperadmin && user.role === filterRole));
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleSort = (col: typeof sortColumn) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const sortedUsers = React.useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredUsers].sort((a, b) => {
      switch (sortColumn) {
        case 'user':
          return dir * (a.name || a.email).localeCompare(b.name || b.email);
        case 'account': {
          const aName = a.isSuperadmin ? '___superadmin' : (a.accountName || 'zzz');
          const bName = b.isSuperadmin ? '___superadmin' : (b.accountName || 'zzz');
          return dir * aName.localeCompare(bName);
        }
        case 'role': {
          const aRole = a.isOwner ? 'owner' : a.isSuperadmin ? 'superadmin' : (a.role || 'zzz');
          const bRole = b.isOwner ? 'owner' : b.isSuperadmin ? 'superadmin' : (b.role || 'zzz');
          return dir * aRole.localeCompare(bRole);
        }
        case 'status':
          return dir * a.status.localeCompare(b.status);
        case 'lastLogin': {
          const aDate = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          const bDate = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          return dir * (aDate - bDate);
        }
        default: return 0;
      }
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  // Get available roles based on current user
  const getAvailableRoles = () => {
    if (currentUser?.role === 'superadmin') {
      return ROLE_OPTIONS;
    }
    // Non-superadmins can't assign superadmin or admin roles
    return ROLE_OPTIONS.filter(r => r.value !== 'superadmin' && r.value !== 'admin');
  };

  // Create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name) {
      setMessage({ type: 'error', text: 'Email and name are required' });
      return;
    }

    setProcessing(true);
    const userEmail = createForm.email.toLowerCase();
    try {
      // Create user with account and role (backend also creates Firebase Auth user)
      await createUser({
        email: userEmail,
        name: createForm.name,
        isSuperadmin: createForm.isSuperadmin,
        accountId: createForm.isSuperadmin ? undefined : createForm.accountId,
        role: createForm.isSuperadmin ? undefined : createForm.role,
      });

      setMessage({
        type: 'success',
        text: `User created! Password reset email sent to ${userEmail}.`,
      });
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        name: '',
        isSuperadmin: false,
        accountId: accounts[0]?.id || '',
        role: 'viewer',
      });
      fetchData();
    } catch (error: any) {
      console.error('Error creating user:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to create user' });
    } finally {
      setProcessing(false);
    }
  };

  // Update user
  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;

    setProcessing(true);
    try {
      await updateUser(editForm.id, {
        name: editForm.name,
        isSuperadmin: editForm.isSuperadmin,
        accountId: editForm.accountId || undefined,
        role: editForm.role || undefined,
        status: editForm.status,
        ...(currentUser?.role === 'superadmin' ? { ssoExempt: editForm.ssoExempt } : {}),
      });

      setMessage({ type: 'success', text: 'User updated successfully!' });
      setShowEditModal(false);
      setEditForm(null);
      fetchData();
    } catch (error: any) {
      console.error('Error updating user:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update user' });
    } finally {
      setProcessing(false);
    }
  };

  // Delete user
  // Send password reset email
  const handleSendPasswordReset = async (userId: string, email: string) => {
    if (!confirm(`Send password reset email to ${email}?`)) return;
    
    setSendingResetTo(email);
    try {
      // Backend ensures Firebase Auth user exists and sends email server-side
      await sendPasswordReset(userId);
      setMessage({ type: 'success', text: `Password reset email sent to ${email}` });
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to send password reset email' });
    } finally {
      setSendingResetTo(null);
    }
  };

  const handleCopyResetLink = async (userId: string, email: string, name: string) => {
    setCopyingLinkFor(email);
    try {
      const link = await getResetLink(userId);
      const text = `Hi ${name || email},\nHere is your password reset link for SpotLock:\n${link}`;
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: `Reset link for ${email} copied to clipboard` });
    } catch (error: any) {
      console.error('Error getting reset link:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to generate reset link' });
    } finally {
      setCopyingLinkFor(null);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await deleteUser(userId);
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      fetchData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete user' });
    } finally {
      setProcessing(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-gradient-to-r from-purple-200 to-pink-200 text-purple-900';
      case 'superadmin': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-yellow-100 text-yellow-800';
      case 'manager': return 'bg-green-100 text-green-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            {filteredUsers.length} users {filterRole !== 'all' && '(filtered)'}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded"
        >
          + Create User
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-3 mb-4 rounded ${
            message.type === 'error'
              ? 'bg-red-50 border border-red-200 text-red-800'
              : 'bg-green-50 border border-green-200 text-green-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <input
          type="text"
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
        >
          <option value="all">All Roles</option>
          {ROLE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {([
                  ['user', 'User'],
                  ['account', 'Account'],
                  ['role', 'Role'],
                  ['status', 'Status'],
                  ['lastLogin', 'Last Login'],
                ] as const).map(([col, label]) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortColumn === col && (
                        sortDirection === 'asc'
                          ? <ArrowUpward sx={{ fontSize: 14 }} />
                          : <ArrowDownward sx={{ fontSize: 14 }} />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-medium">
                          {(user.name || user.email)[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name || 'No name'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {user.isSuperadmin
                      ? <span className="italic text-purple-600">All accounts</span>
                      : (user.accountName || <span className="text-gray-400">—</span>)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.isOwner ? (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-200 to-pink-200 text-purple-900 border border-purple-300">
                        Owner
                      </span>
                    ) : user.isSuperadmin ? (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-purple-100 text-purple-800">
                        Superadmin
                      </span>
                    ) : user.role ? (
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {ROLE_OPTIONS.find(r => r.value === user.role)?.label || user.role}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">No role</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                      user.status === 'active' 
                        ? 'bg-green-100 text-green-800 border-green-300' :
                      user.status === 'pending' 
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                      user.status === 'suspended' 
                        ? 'bg-red-100 text-red-800 border-red-300' :
                        'bg-gray-100 text-gray-800 border-gray-300'
                    }`}>
                      {user.status === 'active' && '✓ '}
                      {user.status === 'pending' && '⏳ '}
                      {user.status === 'suspended' && '⛔ '}
                      {user.status === 'deactivated' && '🚫 '}
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLogin ? (
                      <div className="flex flex-col">
                        <span>{new Date(user.lastLogin).toLocaleDateString()}</span>
                        <span className="text-xs text-gray-400">{user.loginCount} logins</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">Never</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditForm({
                            id: user.id,
                            email: user.email,
                            name: user.name || '',
                            isSuperadmin: user.isSuperadmin,
                            accountId: user.accountId,
                            accountName: user.accountName,
                            role: user.role,
                            status: user.status,
                            ssoExempt: !!user.ssoExempt,
                            lastLogin: user.lastLogin,
                            loginCount: user.loginCount,
                            createdAt: user.createdAt,
                          });
                          setShowEditModal(true);
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit user"
                      >
                        <Edit fontSize="small" />
                      </button>
                      <button
                        onClick={() => handleSendPasswordReset(user.id, user.email)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Send password reset email"
                        disabled={sendingResetTo === user.email}
                      >
                        <Email fontSize="small" />
                      </button>
                      <button
                        onClick={() => handleCopyResetLink(user.id, user.email, user.name || '')}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                        title="Copy reset link to clipboard"
                        disabled={copyingLinkFor === user.email}
                      >
                        <VpnKey fontSize="small" />
                      </button>
                      {currentUser?.isAdmin && (
                        <button
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete user"
                          disabled={processing}
                        >
                          <Delete fontSize="small" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No users found
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              {currentUser?.isAdmin && (
                <div className="mb-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={createForm.isSuperadmin}
                      onChange={(e) => setCreateForm({ ...createForm, isSuperadmin: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Superadmin (Global Access)</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Superadmins have full access to all accounts
                  </p>
                </div>
              )}
              {!createForm.isSuperadmin && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
                    <select
                      value={createForm.accountId}
                      onChange={(e) => setCreateForm({ ...createForm, accountId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      required
                      disabled={!currentUser?.isAdmin}
                    >
                      <option value="">Select account...</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                    {!currentUser?.isAdmin && (
                      <p className="text-xs text-gray-500 mt-1">
                        You can only create users in your own account
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                      required
                    >
                      {ROLE_OPTIONS.filter(r => r.value !== 'superadmin').map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {ROLE_OPTIONS.find(r => r.value === createForm.role)?.description}
                    </p>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded disabled:opacity-50"
                >
                  {processing ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit User</h2>
            <form onSubmit={handleUpdateUser}>
              {/* User Info Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">User Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editForm.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Access Control Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Access Control</h3>
                <div className="space-y-4">
                  {currentUser?.isAdmin && (
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editForm.isSuperadmin}
                          onChange={(e) => setEditForm({ ...editForm, isSuperadmin: e.target.checked })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Superadmin</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        {editForm.isSuperadmin ? 'Has global access to all accounts' : 'Access controlled by account assignment'}
                      </p>
                    </div>
                  )}
                  {!editForm.isSuperadmin && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                        <select
                          value={editForm.accountId || ''}
                          onChange={(e) => setEditForm({ ...editForm, accountId: e.target.value || null })}
                          disabled
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        >
                          <option value="">Select account...</option>
                          {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {editForm.accountName || 'No account assigned'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={editForm.role || 'viewer'}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                        >
                          {ROLE_OPTIONS.filter(r => r.value !== 'superadmin').map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {ROLE_OPTIONS.find(r => r.value === editForm.role)?.description}
                        </p>
                      </div>
                      {currentUser?.role === 'superadmin' && (
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.ssoExempt}
                            onChange={(e) => setEditForm({ ...editForm, ssoExempt: e.target.checked })}
                            className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-700">Allow sign-in without SSO</span>
                            <span className="block text-xs text-gray-500">
                              Lets this user sign in with Google/email even if the account requires SSO (e.g. a Spotlock team member in the Strauss account).
                            </span>
                          </span>
                        </label>
                      )}
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, status: 'active' })}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          editForm.status === 'active'
                            ? 'bg-green-100 border-green-500 text-green-800 font-semibold'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-green-300'
                        }`}
                      >
                        ✓ Active
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, status: 'pending' })}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          editForm.status === 'pending'
                            ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-semibold'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-yellow-300'
                        }`}
                      >
                        ⏳ Pending
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, status: 'suspended' })}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          editForm.status === 'suspended'
                            ? 'bg-red-100 border-red-500 text-red-800 font-semibold'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'
                        }`}
                      >
                        ⛔ Suspended
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, status: 'deactivated' })}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          editForm.status === 'deactivated'
                            ? 'bg-gray-200 border-gray-500 text-gray-800 font-semibold'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        🚫 Deactivated
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">User Analytics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Created</div>
                    <div className="text-sm font-medium text-gray-900">
                      {new Date(editForm.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Last Login</div>
                    <div className="text-sm font-medium text-gray-900">
                      {editForm.lastLogin 
                        ? new Date(editForm.lastLogin).toLocaleDateString()
                        : 'Never'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Total Logins</div>
                    <div className="text-sm font-medium text-gray-900">
                      {editForm.loginCount || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditForm(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="cursor-pointer border-white text-white bg-purple text-sm border-1 py-[0.3rem] hover:brightness-95 px-[0.625rem] rounded disabled:opacity-50"
                >
                  {processing ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
